import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import b from 'b_';
import React, { Component, Fragment } from 'react';
import { CONTROL_MODE } from '../../constants';
import FuseSelectStore from '../../stores/FuseSelectStore';
import { FTLabel } from '../Fuse';
import Icon from '../Icon/Icon';
import Pill, { PILL } from '../Pill/Pill';
import UDSpinner from '../UD/Fragments/UDSpinner';
import FuseReadOnlyComponent from './FuseReadOnlyComponent';
import { ObservableFuseSelect } from './FuseSelect';
import './FuseReadOnlySelect.css';

const block = b.lock('FuseReadOnlySelect');

/**
 * This is a fuse radio button with the ReadOnly property changed to render what we need it to, according to
 * the new policy specs. See FuseReadOnlyComponent for a better description of the reason we do this.
 * */
class FuseReadOnlySelect extends Component {
    static propTypes = {
        mode: PropTypes.oneOf([CONTROL_MODE.AUDIT]),
        // Only applicable if mode is Audit
        diff: PropTypes.shape({
            added: PropTypes.array,
            removed: PropTypes.array,
        }),
        placeholder: PropTypes.string,
        ...FuseReadOnlyComponent.PropType,
        ...ObservableFuseSelect.propTypes,
        store: FuseSelectStore.PropType, // remove requirement, store is not used in AUDIT mode
    };

    renderPillIcon(icon) {
        return icon ? <Icon type={icon} style={{ width: 16, height: 16, marginRight: 4 }} /> : null;
    }

    renderPills = () => {
        const { store, loading } = this.props;
        const itemsMap = store.options.reduce((acc, item) => {
            acc[item.value] = item;
            return acc;
        }, {});
        let sortedValues = this.props.store.selectedValues;
        // To reuse FuseSelect's sorter we make compromises. E.g. we must sort the values, not the full entities
        if (store.sorterFactory) {
            sortedValues = sortedValues.sort(store.sorterFactory(store.options, itemsMap));
        }
        const arr = sortedValues.map((v) => itemsMap[v]).filter(Boolean);

        return <div className={cn(block('PillContainer'), this.props.containerClassName)}>
            {loading ? <UDSpinner
                className={block('PillLoader')}
                iconClassName={block('Spinner')}
                type="fuse"
            /> : <Fragment>
                {arr.length === 0 ? <div> {this.props.placeholder} </div> : null}
                {arr.map(({ label, icon, hidden, error }, i) => {
                    return <Pill
                        className={block('Pill')}
                        key={i}
                        type={(!hidden && !error) ? PILL.GRAY : PILL.RED}
                    >
                        {this.renderPillIcon(icon)}
                        {label}
                    </Pill>;
                })}
            </Fragment>}
        </div>;
    }

    renderDiffPill = (isAdded) => (entry) => <Pill
        className={cn(block('Pill'))}
        key={entry.id || entry}
        type={isAdded ? PILL.GREEN : PILL.RED}
        iconLeft={<Icon className={block('Icon')} type={isAdded ? 'add_circle' : 'do_disturb_on'} />}
    >
        {this.renderPillIcon(entry.icon)}
        {entry.label || entry.name || entry.id || entry}
    </Pill>

    renderAddedPill = this.renderDiffPill(true)
    renderRemovedPill = this.renderDiffPill(false)

    renderAuditDiff = ({ added, removed }) => {
        if (!added.length && !removed.length) {
            return null;
        }
        return <Fragment>
            {this.props.placeholder ? <FTLabel>{this.props.placeholder}</FTLabel> : null}
            <div className={cn(block('PillContainer'), this.props.containerClassName)}>
                {added.map(this.renderAddedPill)}
                {removed.map(this.renderRemovedPill)}
            </div>
        </Fragment>;
    }

    render = () => (this.props.mode === CONTROL_MODE.AUDIT ?
        this.renderAuditDiff(this.props.diff) :
        <FuseReadOnlyComponent
            component={ObservableFuseSelect}
            readOnlyValue={this.props.readOnly ? this.renderPills() : null}
            {...this.props}
        />);
}

export default observer(FuseReadOnlySelect);
